/**
 * Client-side reads for CyberSweep board/list fields populated by Gobodo **traversal** skin fields.
 * Most helpers read **Client**-side `ClientCSAuditTrav*` skin fields. For **CS_Audit** → Client
 * projection (`CSAuditClientTravName*`, setup Step 13), use {@link readCSAuditClientNameFromClientTraversal}.
 *
 * **Sort parity with merged audits:** Board and list code sort CSAudits by due date descending
 * (`Due_Date__c` / `DueDate__c` / `custom__due_date` — see `sortCSAuditsByDueDateDesc` in
 * `cyberSweepQueries.ts`). Every Client traversal field created by `setup:gobodo-cybersweep-relationships`
 * must use the **same** one-hop path (`cs_audit_client_rel`, direction `INCOMING` from Client) and the
 * **same** `sortField` / `sortOrder` (due date **DESC**) so the projected row matches `clientAudits[0]`.
 *
 * **Env overrides** (when the graph uses different Client property API names than the setup script):
 * - `REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_PHASE_STATUS`
 * - `REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_DUE_DATE`
 * - `REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_ROADBLOCK`
 * - `REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_QUARTER`
 * - `REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_FISCAL_YEAR`
 * - `REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_PHASE1_START`
 * - `REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_PHASE2_START`
 * - `REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_AUDITOR`
 * - `REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_AUDIT_TYPE`
 * - `REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_AUDIT_KEY`
 *
 * **Gobodo Fields API:** Traversal skin fields use `type: 'traversal'`. The graph Fields API stores the
 * projection in `traversalQuery` (JSON string) and often surfaces the same payload as `traversalSettings`
 * (object) on read; setup scripts send both so admin and runtime stay in sync. The server evaluates
 * placeholders such as `startObjectId: "${entityid}"` in that JSON; the app does not substitute them.
 */

export type CyberSweepClientBoardTraversalKey =
  | 'phaseStatus'
  | 'dueDate'
  | 'roadblock'
  | 'quarter'
  | 'fiscalYear'
  | 'phase1Start'
  | 'phase2Start'
  | 'auditor'
  | 'auditType'
  | 'auditKey';

const TRAVERSAL_FIELD_ENV: Record<CyberSweepClientBoardTraversalKey, string> = {
  phaseStatus: 'REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_PHASE_STATUS',
  dueDate: 'REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_DUE_DATE',
  roadblock: 'REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_ROADBLOCK',
  quarter: 'REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_QUARTER',
  fiscalYear: 'REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_FISCAL_YEAR',
  phase1Start: 'REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_PHASE1_START',
  phase2Start: 'REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_PHASE2_START',
  auditor: 'REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_AUDITOR',
  auditType: 'REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_AUDIT_TYPE',
  auditKey: 'REACT_APP_CYBERSWEEP_CLIENT_BOARD_FIELD_AUDIT_KEY',
};

/** Default Client API names — must stay in sync with `scripts/gobodo/setup/cybersweep-relationships.ts`. */
export function defaultCyberSweepClientBoardTraversalApiName(
  key: CyberSweepClientBoardTraversalKey,
  customSuffix: string
): string {
  const s = customSuffix;
  switch (key) {
    case 'phaseStatus':
      return `ClientCSAuditTravPhaseStatus${s}`;
    case 'dueDate':
      return `ClientCSAuditTravDueDate${s}`;
    case 'roadblock':
      return `ClientCSAuditTravRoadblock${s}`;
    case 'quarter':
      return `ClientCSAuditTravQuarter${s}`;
    case 'fiscalYear':
      return `ClientCSAuditTravFiscalYear${s}`;
    case 'phase1Start':
      return `ClientCSAuditTravPhase1Start${s}`;
    case 'phase2Start':
      return `ClientCSAuditTravPhase2Start${s}`;
    case 'auditor':
      return `ClientCSAuditTravAuditor${s}`;
    case 'auditType':
      return `ClientCSAuditTravAuditType${s}`;
    case 'auditKey':
      return `ClientCSAuditTravAuditKey${s}`;
    default:
      return '';
  }
}

export function getCyberSweepClientBoardTraversalApiName(
  key: CyberSweepClientBoardTraversalKey,
  customSuffix: string
): string {
  const envName = TRAVERSAL_FIELD_ENV[key];
  const fromEnv = envName ? process.env[envName] : undefined;
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv).trim();
  return defaultCyberSweepClientBoardTraversalApiName(key, customSuffix);
}

function traversalValueToDisplayString(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    const id = o.id ?? o.key ?? o.Id ?? o.Key;
    if (id !== undefined && id !== null) return String(id).trim();
    return '';
  }
  return String(value).trim();
}

/**
 * Read a traversal-backed Client field from the record root or `client.data`.
 */
export function readCyberSweepClientTraversalField(
  client: Record<string, unknown>,
  data: Record<string, unknown>,
  key: CyberSweepClientBoardTraversalKey,
  customSuffix: string
): unknown {
  const name = getCyberSweepClientBoardTraversalApiName(key, customSuffix);
  if (!name) return undefined;
  if (client[name] !== undefined && client[name] !== null) return client[name];
  if (data[name] !== undefined && data[name] !== null) return data[name];
  return undefined;
}

export function readCyberSweepClientTraversalString(
  client: Record<string, unknown>,
  data: Record<string, unknown>,
  key: CyberSweepClientBoardTraversalKey,
  customSuffix: string
): string {
  return traversalValueToDisplayString(
    readCyberSweepClientTraversalField(client, data, key, customSuffix)
  );
}

const CSAUDIT_TRAV_CLIENT_NAME_ENV = 'REACT_APP_CYBERSWEEP_CSAUDIT_TRAV_FIELD_CLIENT_NAME';

/**
 * API name of the CS_Audit → Client traversal that projects the linked **Client** `Name__c`
 * (see `scripts/gobodo/setup/cybersweep-relationships.ts` — `CSAuditClientTravName{suffix}`).
 * Override via {@link CSAUDIT_TRAV_CLIENT_NAME_ENV} if your graph uses a different field name.
 */
export function getCSAuditClientNameTraversalFieldApiName(customSuffix: string): string {
  const fromEnv = process.env[CSAUDIT_TRAV_CLIENT_NAME_ENV];
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv).trim();
  return `CSAuditClientTravName${customSuffix}`;
}

/**
 * Read the projected **client / account name** from a CS_Audit record when the Gobodo graph has run
 * the `cs_audit_client_rel` traversal (same source as the main CyberSweep client board, but on the
 * audit row). Use this for audit-centric lists (My Work, team views) so we do not depend on a separate
 * Client fetch when the traversal field is materialized.
 */
export function readCSAuditClientNameFromClientTraversal(
  audit: Record<string, unknown>,
  customSuffix: string
): string {
  const name = getCSAuditClientNameTraversalFieldApiName(customSuffix);
  if (!name) return '';
  const data = (audit.data as Record<string, unknown>) ?? {};
  const raw = audit[name] ?? data[name];
  return traversalValueToDisplayString(raw);
}

/** True when the Gobodo payload includes a traversal skin field (even if the value is null). */
export function clientRecordExposesCyberSweepBoardTraversalField(
  client: Record<string, unknown>,
  key: CyberSweepClientBoardTraversalKey,
  customSuffix: string
): boolean {
  const name = getCyberSweepClientBoardTraversalApiName(key, customSuffix);
  if (!name) return false;
  const data = (client.data as Record<string, unknown>) ?? client;
  return Object.prototype.hasOwnProperty.call(client, name) || Object.prototype.hasOwnProperty.call(data, name);
}

/**
 * True when any loaded Client row includes CSAudit traversal skin fields in the payload (same probe as board:
 * `phaseStatus` or `auditKey`). Used to skip legacy list `useCSAudits` when the graph already projects latest audit.
 */
export function clientRecordsExposesCyberSweepListTraversalFields(
  records: Record<string, unknown>[],
  customSuffix: string
): boolean {
  for (const client of records) {
    if (!client) continue;
    if (
      clientRecordExposesCyberSweepBoardTraversalField(client, 'phaseStatus', customSuffix) ||
      clientRecordExposesCyberSweepBoardTraversalField(client, 'auditKey', customSuffix)
    ) {
      return true;
    }
  }
  return false;
}
